import { Column, Row } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useCallback } from "react";
import { SHTableBody } from "@components/design-systems";
import { MSTableBodyRow } from "src/pages/managed-accounts/model-screener/components/model-screener-table/table/table-body/table-body-row";
import { ModelScreenerRow } from "@models/model-screener/entities/modelScreenerData";
import { getMSTableVirtualizerSetup } from "@pages/managed-accounts/model-screener/utils";

type ModelScreenerTableBodyProps = {
  rows: Row<ModelScreenerRow>[];
  columns: Column<ModelScreenerRow>[];
  containerRefElement: HTMLDivElement | null;
};

export const ModelScreenerTableBody = React.memo(
  ({ rows, columns, containerRefElement }: ModelScreenerTableBodyProps) => {
    const {
      rowOverscan,
      columnOverscan,
      rowEstimateSizeFunction,
      columnEstimateSizeFunction,
      measureElementHeight,
      measureElementWidth,
    } = getMSTableVirtualizerSetup({ columns });

    const rowVirtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => containerRefElement,
      estimateSize: rowEstimateSizeFunction,
      measureElement: measureElementHeight,
      overscan: rowOverscan,
    });
    const rowVirtualizerItems = rowVirtualizer.getVirtualItems();
    const rowVirtualizerTotalSize = rowVirtualizer.getTotalSize();

    const getColumnWidthCallBack = useCallback(
      (index: number) => columnEstimateSizeFunction(index),
      [columnEstimateSizeFunction],
    );
    const columnVirtualizer = useVirtualizer({
      count: columns.length,
      getScrollElement: () => containerRefElement,
      estimateSize: getColumnWidthCallBack,
      horizontal: true,
      measureElement: measureElementWidth,
      overscan: columnOverscan,
    });
    const columnVirtualizerItems = columnVirtualizer.getVirtualItems();

    return (
      <SHTableBody
        style={{
          display: "grid",
          height: `${rowVirtualizerTotalSize}px`,
          position: "relative",
        }}
      >
        {rowVirtualizerItems.map((virtualRow) => {
          const row: Row<ModelScreenerRow> = rows[virtualRow.index];

          return (
            <MSTableBodyRow
              key={row.original.id}
              row={row}
              virtualRow={virtualRow}
              rowVirtualizer={rowVirtualizer}
              columns={columns}
              columnVirtualizerItems={columnVirtualizerItems}
            />
          );
        })}
      </SHTableBody>
    );
  },
);
